import { Controller } from "@hotwired/stimulus"

// Sliding active-tab indicator for shared/components/_tab_bar (sliding: true).
//
// The tabs are ordinary links, so selecting one is a full Turbo page visit that
// replaces the markup this controller is attached to. To keep the slide visible
// across that swap we stash the position the bar is *leaving from* in
// sessionStorage on click; the next page's connect() starts the bar there and
// animates to the new active tab on the following frame. Either half alone
// would be enough when the visit is slow (the click animation) or fast (the
// replay) — together the motion reads the same at any speed.
//
// Progressive enhancement: shared/_ajax_tab renders a real border-bottom under
// the active tab, so the underline is correct with JS off. This controller
// strips that static border on connect and takes over with the animated bar.
const STORAGE_PREFIX = "sliding-tabs:"
// A hand-off older than this means the visit outlasted the click's slide.
const REPLAY_WINDOW_MS = 400
const STATIC_UNDERLINE_CLASSES = ["border-b-2", "border-core-purple-500", "border-purple", "mb-[-2px]"]
const INDICATOR_CLASSES = [
  "absolute", "bottom-0", "left-0", "w-0", "h-[2px]", "bg-core-purple-500",
  "pointer-events-none", "transition-[transform,width]", "duration-200",
  "ease-out", "motion-reduce:transition-none"
]

export default class extends Controller {
  static targets = ["row"]

  connect() {
    this.indicator = this.buildIndicator()
    if (!this.indicator) return

    const active = this.activeMetrics()
    const stored = this.readStored()
    this.clearStored()

    // Replay only a fresh hand-off: on a visit slower than the slide, the
    // click already carried the bar all the way, so replaying would jump it
    // back and slide it a second time.
    const fresh = stored && typeof stored.at === "number" && Date.now() - stored.at < REPLAY_WINDOW_MS
    if (fresh && active && this.moved(stored, active)) {
      this.place(stored, false)
      this.frame = this.requestFrame(() => this.place(this.activeMetrics(), true))
    } else {
      this.place(active, false)
    }

    // The bar lives inside the scrolled row, so scrolling carries it along on
    // its own. What can move it is a size change -- a late web-font swap, the
    // container resizing, a hidden bar becoming visible -- so watch the row.
    this.reposition = () => this.place(this.activeMetrics(), false)
    const Observer = this.view?.ResizeObserver
    if (Observer) {
      // ResizeObserver reports once on observe() even when nothing changed;
      // acting on that would snap the bar mid-slide. React only to a real
      // change of the row's size.
      this.observedSize = null
      this.observer = new Observer(entries => {
        const box = entries?.[0]?.contentRect
        const size = box ? `${box.width}x${box.height}` : null
        const first = this.observedSize === null
        this.observedSize = size
        if (first && this.lastMetrics) return
        this.reposition()
      })
      this.observer.observe(this.row)
    } else {
      this.view?.addEventListener("resize", this.reposition)
    }
  }

  disconnect() {
    if (this.frame !== undefined) this.view?.cancelAnimationFrame?.(this.frame)
    this.observer?.disconnect()
    this.observer = null
    this.view?.removeEventListener("resize", this.reposition)
    this.indicator?.remove()
    this.indicator = null
  }

  // Delegated click from the tab row.
  select(event) {
    // A modified click opens the tab elsewhere and leaves this page as it is.
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || (event.button ?? 0) !== 0) return
    const tab = event.target?.closest?.("a")
    if (!tab || !this.tabs.includes(tab) || tab === this.activeTab) return

    this.store({ ...(this.lastMetrics || this.activeMetrics()), at: Date.now() })
    this.place(this.metricsFor(tab), true)
  }

  // AJAX panels keep this controller mounted. Their selection event carries the
  // new aria-current state, so no cross-page position hand-off is needed.
  sync(event) {
    this.place(this.activeMetrics(), event.detail?.animate !== false)
  }

  // --- geometry -------------------------------------------------------------

  get row() {
    return this.hasRowTarget ? this.rowTarget : this.element.firstElementChild
  }

  get tabs() {
    const row = this.row
    if (!row) return []
    return Array.from(row.children).filter(child => child.tagName === "A")
  }

  get activeTab() {
    return this.tabs.find(tab => tab.getAttribute("aria-current") === "page" || tab.dataset.active === "true")
  }

  activeMetrics() {
    return this.metricsFor(this.activeTab)
  }

  metricsFor(tab) {
    const row = this.row
    if (!row || !tab) return null

    const rowRect = row.getBoundingClientRect()
    // Measure the label box (shared/_ajax_tab's inner div, inset by mx-4), not
    // the link, so the bar matches the width of the static underline it replaces.
    const tabRect = (tab.firstElementChild || tab).getBoundingClientRect()
    if (!tabRect.width) return null

    return { left: tabRect.left - rowRect.left, width: tabRect.width }
  }

  moved(from, to) {
    return Math.abs(from.left - to.left) + Math.abs(from.width - to.width) > 0.5
  }

  place(metrics, animate) {
    if (!this.indicator) return
    // Nothing measurable yet (e.g. a hidden container): leave the server-rendered
    // underline in place rather than showing no underline at all.
    if (!metrics) return
    this.removeStaticUnderlines()

    const animated = animate && !this.reducedMotion
    // Jumping without a transition means killing it for exactly this write.
    if (!animated) this.indicator.style.transitionProperty = "none"
    this.indicator.style.transform = `translateX(${metrics.left}px)`
    this.indicator.style.width = `${metrics.width}px`
    if (!animated) {
      void this.indicator.offsetWidth // flush the jump so the next move animates
      this.indicator.style.transitionProperty = ""
    }

    this.lastMetrics = metrics
  }

  // --- DOM ------------------------------------------------------------------

  buildIndicator() {
    const row = this.row
    if (!row) return null

    // Reuse rather than stack up a second bar when Turbo restores a cached page.
    let bar = row.querySelector("[data-sliding-tabs-indicator]")
    if (bar) return bar

    bar = this.element.ownerDocument.createElement("div")
    bar.setAttribute("data-sliding-tabs-indicator", "")
    bar.setAttribute("aria-hidden", "true")
    bar.classList.add(...INDICATOR_CLASSES)
    row.appendChild(bar)
    return bar
  }

  removeStaticUnderlines() {
    this.tabs.forEach(tab => {
      tab.classList.remove("mb-[-2px]")
      tab.querySelectorAll(".border-core-purple-500, .border-purple").forEach(element => {
        element.classList.remove(...STATIC_UNDERLINE_CLASSES)
      })
    })
  }

  // --- environment ----------------------------------------------------------

  get view() {
    return this.element.ownerDocument.defaultView
  }

  get reducedMotion() {
    const view = this.view
    return typeof view?.matchMedia === "function" && view.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  requestFrame(callback) {
    const view = this.view
    if (typeof view?.requestAnimationFrame === "function") return view.requestAnimationFrame(callback)
    return view?.setTimeout(callback, 0)
  }

  // --- cross-visit position handoff ----------------------------------------

  get storageKey() {
    return `${STORAGE_PREFIX}${this.element.id || "default"}`
  }

  store(metrics) {
    if (typeof metrics?.left !== "number") return
    try {
      this.view?.sessionStorage?.setItem(this.storageKey, JSON.stringify(metrics))
    } catch (_error) {
      // Storage can be unavailable (private mode, blocked cookies) — the bar
      // then simply has no previous position to slide from.
    }
  }

  readStored() {
    try {
      const raw = this.view?.sessionStorage?.getItem(this.storageKey)
      if (!raw) return null
      const parsed = JSON.parse(raw)
      if (typeof parsed?.left !== "number" || typeof parsed?.width !== "number") return null
      return parsed
    } catch (_error) {
      return null
    }
  }

  clearStored() {
    try {
      this.view?.sessionStorage?.removeItem(this.storageKey)
    } catch (_error) {
      // See store().
    }
  }
}
